import { Grid } from '@mui/material';
import { ThemePalette } from 'mui.theme';
import { CustomTabPanel } from 'views/Production/CustomPanel/index';
import ProductiveHours from '../components/ProductiveHours';
import DateRangeDiv from 'views/Utilization/HeaderMenu/DateRangeDiv';
import GridAnalytics from '../components/GridAnalytics';
import HoursBreakdown from '../components/HoursBreakdown';
import FuelConsumption from '../components/FuelConsumption';
import Suggestions from 'views/Utilization/components/Suggestions/Suggestions';
import AssetView from '../components/Asset/AssetWrapper';
import type { utilizationLoadingStates } from 'types/utilization.types';
import { aggregateFuelConsumption } from '../AllFleet';
import { getShiftHours } from '../utils';
import { memo, useMemo } from 'react';
import { useAppSelector } from 'store/hook';
import useAppTheme from 'hooks/useAppTheme';
import useCustomerInfo from 'hooks/useCustomerInfo';
import { useTranslation } from 'react-i18next';
import { CLIENT_THEME, clientThemePalette } from 'utils/clientTheme';

interface Props {
  value: number;
  isSelected: string;
  setIsSelected: any;
  handleDateChange: any;
  startDate: any;
  setStartDate: any;
  endDate: any;
  setEndDate: any;
  yesterday: any;
  assetOverview: any;
  loadingStates: utilizationLoadingStates;
  aggregation: string;
  setAggregation: any;
  shiftNumber: number;
  setShiftNumber: any;
  shiftWiseAnalysis: boolean;
  setShiftWiseAnalysis: any;
  utilizationType: string;
  setUtilizationType: any;
  setDetailsVal: any;
  setAnalyticsData: any;
  setHoursBreakDown: any;
  setUserSelections: any;
  setSuggestionData: any;
  setFuelConsumption: any;
  selectedChartSource: any;
  setSelectedChartSource: any;
  handleFetchCustomDateRangeData: ({
    aggregation,
  }: {
    aggregation: string;
  }) => void;
  handleFetchCustomOnAggregationChange: ({
    aggregation,
    startDate,
    endDate,
  }: {
    aggregation: string;
    startDate: Date;
    endDate: Date;
  }) => void;
}

const Loader = ({
  value,
  isSelected,
  setIsSelected,
  handleDateChange,
  startDate,
  setStartDate,
  endDate,
  setEndDate,
  yesterday,
  assetOverview,
  loadingStates,
  aggregation,
  setAggregation,
  shiftWiseAnalysis,
  setShiftWiseAnalysis,
  shiftNumber,
  setShiftNumber,
  utilizationType,
  setUtilizationType,
  setDetailsVal,
  setAnalyticsData,
  setHoursBreakDown,
  setUserSelections,
  setSuggestionData,
  setFuelConsumption,
  selectedChartSource,
  setSelectedChartSource,
  handleFetchCustomDateRangeData,
  handleFetchCustomOnAggregationChange,
}: Props) => {
  const { t } = useTranslation();
  const { enableDarkTheme } = useAppTheme();
  const palette = enableDarkTheme
    ? clientThemePalette[CLIENT_THEME].dark
    : clientThemePalette[CLIENT_THEME].light;
  const { customerCode } = useCustomerInfo();

  const data = useAppSelector(
    (state) => state.utilizationReducer.data.allFleet
  );

  const dataSummary = useAppSelector(
    (state) => state.utilizationReducer.data.allFleet.summary
  );

  const dataAssets = useAppSelector(
    (state) => state.utilizationReducer.data.allFleet.assets
  );

  const summaryData = useMemo(() => {
    return {
      utilization:
        dataSummary.loader_avg_utilization_engine_by_shift_hours * 100,
      utilizationDiff:
        dataSummary.loader_avg_utilization_engine_by_shift_hours_diff,
      idling: dataSummary.loader_avg_idling_duration,
      idlingDiff: dataSummary.loader_avg_idling_duration_diff,
      shiftHours: getShiftHours(customerCode) * 60, // 10.5 in mins,
      shiftHoursDiff: 0,
      engineHours: dataSummary.loader_avg_utilization_engine_working_minute,
      engineHoursDiff:
        dataSummary.loader_avg_utilization_engine_working_minute_diff,
      engineActiveMinutes:
        dataSummary.loader_avg_utilization_engine_active_minute,
      engineActiveMinutesDiff:
        dataSummary.loader_avg_utilization_engine_active_minute_diff,
    };
  }, [dataSummary]);

  const loaderBreakDown = useMemo(() => {
    return {
      productiveHours: dataSummary.loader_total_engine_working_minutes,
      productiveHoursDiff: dataSummary.loader_total_engine_working_minutes_diff,
      idling: dataSummary.loader_total_idling_minutes,
      idlingDiff: dataSummary.loader_total_idling_minutes_diff,
      noOfAssets: dataSummary.number_of_loaders,
      dateRange: dataSummary.date_range,
      engineHours: dataSummary.loader_total_engine_active_minutes,
      shiftHours: dataSummary.loader_total_shift_hours,
      engineHoursDiff: dataSummary.loader_total_engine_active_minutes_diff,
      shiftHoursDiff: dataSummary.loader_total_shift_hours_diff,
    };
  }, [dataSummary]);

  const fuelData = useMemo(() => {
    return {
      total:
        Number(dataSummary.loader_total_productive_fuel_consumption_l) +
        Number(dataSummary.loader_total_idle_fuel_consumption_l),
      totalDiff:
        (Number(dataSummary.loader_total_productive_fuel_consumption_l_diff) *
          Number(dataSummary.loader_total_idle_fuel_consumption_l_diff)) /
        100,
      productive: dataSummary.loader_total_productive_fuel_consumption_l,
      productiveDiff:
        dataSummary.loader_total_productive_fuel_consumption_l_diff,
      idle: dataSummary.loader_total_idle_fuel_consumption_l,
      idleDiff: dataSummary.loader_total_idle_fuel_consumption_l_diff,
    };
  }, [dataSummary]);

  const loaderAssets = useMemo(() => {
    return dataAssets.filter((item) => ['loader'].includes(item.asset_type));
  }, [dataAssets]);

  const aggregatedData = useMemo(() => {
    return aggregateFuelConsumption(loaderAssets);
  }, [loaderAssets]);

  return (
    <>
      <CustomTabPanel value={value} index={2}>
        <div>
          <ProductiveHours
            chartData={data.fleetPeriodSummary.loaders}
            detailsData={data.fleetDetails.loaders}
            assetType="loader"
            setDetailsVal={setDetailsVal}
            setUserSelections={setUserSelections}
            selectedChartSource={selectedChartSource}
            setSelectedChartSource={setSelectedChartSource}
          />
          <DateRangeDiv
            isSelected={isSelected}
            setIsSelected={setIsSelected}
            handleDateChange={handleDateChange}
            startDate={startDate}
            setStartDate={setStartDate}
            endDate={endDate}
            setEndDate={setEndDate}
            yesterday={yesterday}
            aggregation={aggregation}
            setAggregation={setAggregation}
            shiftWiseAnalysis={shiftWiseAnalysis}
            setShiftWiseAnalysis={setShiftWiseAnalysis}
            shiftNumber={shiftNumber}
            setShiftNumber={setShiftNumber}
            utilizationType={utilizationType}
            setUtilizationType={setUtilizationType}
            handleFetchCustomDateRangeData={handleFetchCustomDateRangeData}
            handleFetchCustomOnAggregationChange={
              handleFetchCustomOnAggregationChange
            }
          />

          <GridAnalytics
            assetOverview={assetOverview}
            isLoading={loadingStates.isLoadingFleetSummary}
            summary={summaryData}
            isSelected={isSelected}
            score={data.score}
            data={data.summaryChart.loaders}
            assetType="Loader"
            aggregation={aggregation}
            shiftNumber={shiftNumber}
            shiftWiseAnalysis={shiftWiseAnalysis}
            utilizationType={utilizationType}
            setAnalyticsData={setAnalyticsData}
          />

          <Grid
            container
            spacing={2}
            justifyContent="space-between"
            sx={{
              paddingTop: '15px',
              color: palette.textPrimary,
            }}
          >
            <Grid item lg={9} sm={12} md={12}>
              <Grid container spacing={2} justifyContent="space-between">
                <HoursBreakdown
                  enableDarkTheme={enableDarkTheme}
                  title={t('loader_hours_breakdown')}
                  isLoading={loadingStates.isLoadingFleetSummary}
                  customerCode={customerCode}
                  data={loaderBreakDown}
                  setHoursBreakDown={setHoursBreakDown}
                />
                <FuelConsumption
                  title={t('loader_fuel_consumption')}
                  isLoading={loadingStates.isLoadingFleetSummary}
                  data={fuelData}
                  setFuelConsumption={setFuelConsumption}
                />
              </Grid>
            </Grid>
            <Suggestions
              minHeight="400px"
              data={aggregatedData}
              assetType="loader"
              setSuggestionData={setSuggestionData}
            />
          </Grid>
          <AssetView
            isSelected={isSelected}
            data={loaderAssets}
            isLoaderType={true}
            utilizationType={utilizationType}
          />
        </div>
      </CustomTabPanel>
    </>
  );
};

export default memo(Loader);
